import {
  FillingBatchIngredient,
  CreateFillingBatchIngredientRequest,
  UpdateFillingBatchIngredientRequest
} from '../types/fillingBatchIngredient';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3003/api';

export const fillingBatchIngredientApiService = {
  /**
   * Fetch all filling batch ingredients from the backend
   */
  fetchAllFillingBatchIngredients: async (): Promise<FillingBatchIngredient[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/filling-batch-ingredients`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch filling batch ingredients');
      }

      return data.fillingBatchIngredients;
    } catch (error) {
      console.error('Error fetching filling batch ingredients from API:', error);
      throw error;
    }
  },

  /**
   * Fetch filling batch ingredients by filling batch ID
   */
  fetchFillingBatchIngredientsByFillingBatchId: async (fillingBatchId: string): Promise<FillingBatchIngredient[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/filling-batch-ingredients/by-filling-batch/${fillingBatchId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch filling batch ingredients');
      }

      return data.fillingBatchIngredients;
    } catch (error) {
      console.error('Error fetching filling batch ingredients by filling batch ID from API:', error);
      throw error;
    }
  },

  /**
   * Create a new filling batch ingredient
   */
  createFillingBatchIngredient: async (ingredient: CreateFillingBatchIngredientRequest): Promise<FillingBatchIngredient> => {
    try {
      const response = await fetch(`${API_BASE_URL}/filling-batch-ingredients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ingredient)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create filling batch ingredient');
      }

      return data.fillingBatchIngredient;
    } catch (error) {
      console.error('Error creating filling batch ingredient:', error);
      throw error;
    }
  },

  /**
   * Update an existing filling batch ingredient
   */
  updateFillingBatchIngredient: async (id: string, ingredient: UpdateFillingBatchIngredientRequest): Promise<FillingBatchIngredient> => {
    try {
      const response = await fetch(`${API_BASE_URL}/filling-batch-ingredients/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ingredient)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to update filling batch ingredient');
      }

      return data.fillingBatchIngredient;
    } catch (error) {
      console.error('Error updating filling batch ingredient:', error);
      throw error;
    }
  },

  /**
   * Delete a filling batch ingredient by ID
   */
  deleteFillingBatchIngredient: async (id: string): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/filling-batch-ingredients/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to delete filling batch ingredient');
      }
    } catch (error) {
      console.error('Error deleting filling batch ingredient:', error);
      throw error;
    }
  }
};
